import { ChevronsUpDownIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { constants } from '@/lib';

// Mock components and constants
const StatusDot = ({ className }) => <div className={`w-2 h-2 rounded-full ${className}`} />;

/**
 * Combobox - A dropdown select component with keyboard navigation and accessibility features
 *
 * @param {Array} data - Array of options to display. Each item should have:
 *   - value: string (required) - Unique identifier for the option
 *   - label: string (required) - Display text for the option
 *   - dotColor: string (optional) - Tailwind class for colored status dot (e.g., 'bg-green-500')
 *
 * @param {string} placeholder - Text to display when no option is selected
 *
 * @param {Object} selectedData - Currently selected option object from the data array
 *   - Should match one of the items in the data array
 *   - Used to sync external state with the component
 *
 * @param {Function} onChange - Callback function called when selection changes
 *   - Receives the selected object
 *   - Returns empty string ('') when selection is cleared
 *
 * @param {boolean} disabled - If true, disables the combobox interaction
 *
 * @example
 * const data = [
 *   { value: 'active', label: 'Active', dotColor: 'bg-green-500' },
 *   { value: 'pending', label: 'Pending', dotColor: 'bg-yellow-500' }
 * ];
 *
 * <Combobox
 *   data={data}
 *   placeholder="Select status..."
 *   selectedData={data.find(item => item.value === selectedValue)}
 *   onChange={(value) => setSelectedValue(value)}
 *   disabled={false}
 * />
 */
export const Combobox = ({
  data = [],
  placeholder = constants.PLACEHOLDER.SELECT_OPTION,
  selectedData,
  onChange,
  disabled = false,
  dropUp = false,
}) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(selectedData?.value || '');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const isMountedRef = useRef(true);

  // Validate data structure on mount and when data changes
  useEffect(() => {
    if (!Array.isArray(data)) {
      console.error('Combobox: data prop must be an array');
      return;
    }

    // Check for duplicate values
    const values = data.map(item => item?.value).filter(Boolean);
    const duplicates = values.filter((item, index) => values.indexOf(item) !== index);
    if (duplicates.length > 0) {
      console.warn('Combobox: Duplicate values detected in data array', duplicates);
    }
  }, [data]);

  // Sync with prop changes
  useEffect(() => {
    const newValue = selectedData?.value || '';

    // Validate that selectedData exists in data array
    if (selectedData && !data.some(item => item?.value === newValue)) {
      console.warn('Combobox: selectedData does not exist in data array', selectedData);
    }

    setValue(newValue);
  }, [selectedData, data]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (!isMountedRef.current) return;

      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setOpen(false);
        setFocusedIndex(-1);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  // Reset focusedIndex when data changes
  useEffect(() => {
    if (focusedIndex >= data.length) {
      setFocusedIndex(-1);
    }
  }, [data.length, focusedIndex]);

  const selectedLabel = data.find(item => item.value === value);

  const handleSelect = currentValue => {
    try {
      const newValue = currentValue === value ? '' : currentValue;
      const selectedItem = data.find(item => item?.value === newValue);

      setValue(newValue);

      // Call onChange with the full item object or null
      if (onChange && typeof onChange === 'function') {
        onChange(selectedItem || null);
      }

      setOpen(false);
      setFocusedIndex(-1);
    } catch (error) {
      console.error('Combobox: Error in handleSelect', error);
    }
  };

  const handleKeyDown = e => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!open) {
          setOpen(true);
        } else if (focusedIndex >= 0) {
          handleSelect(data[focusedIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        setFocusedIndex(-1);
        buttonRef.current?.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!open) {
          setOpen(true);
        } else {
          setFocusedIndex(prev => (prev < data.length - 1 ? prev + 1 : prev));
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (open) {
          setFocusedIndex(prev => (prev > 0 ? prev - 1 : 0));
        }
        break;
      default:
        break;
    }
  };

  // Safeguard against invalid data
  const safeData = Array.isArray(data) ? data.filter(item => item?.value && item?.label) : [];

  return (
    <div className="relative inline-block w-full">
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        className={`w-full flex justify-between items-center border rounded-md px-4 py-2 text-sm bg hover:bg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
          disabled ? 'cursor-not-allowed' : ''
        }`}
        onClick={() => !disabled && setOpen(prev => !prev)}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby="combobox-label"
      >
        <div className="flex flex-row items-center justify-start gap-3">
          {selectedLabel?.dotColor && <StatusDot className={selectedLabel?.dotColor} />}
          <span className={value === '' ? 'text-muted' : 'text'}>{selectedLabel?.label || placeholder}</span>
        </div>
        <ChevronsUpDownIcon size={13} className="text-muted" />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          ref={dropdownRef}
          className={`absolute w-full bg border border-border rounded-md shadow-lg z-50 max-h-60 overflow-auto ${dropUp ? 'bottom-full mb-1' : 'mt-1'}`}
          role="listbox"
          aria-labelledby="combobox-label"
        >
          {safeData.length === 0 ? (
            <div className="p-3 text-sm text-gray-500 text-center">{constants.NO_DATA.NO_OPTIONS}</div>
          ) : (
            safeData.map((item, index) => (
              <button
                key={item.value}
                type="button"
                role="option"
                aria-selected={value === item.value}
                className={`flex w-full justify-start items-center px-3 py-2 text-sm gap-2 disabled:bg-disabled hover:bg-elevated focus:bg focus:outline-none transition-colors ${
                  value === item.value ? 'bg-elevated' : ''
                } ${focusedIndex === index ? 'bg' : ''}`}
                onClick={() => handleSelect(item.value)}
                onMouseEnter={() => setFocusedIndex(index)}
                disabled={item.disabled}
              >
                <div className="flex items-center gap-2 flex-1">
                  {item?.dotColor && <StatusDot className={item.dotColor} />}
                  <span className="truncate">{item.label}</span>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
